import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  Button,
  Space,
  Tag,
  Select,
  DatePicker,
  Typography,
  Tooltip,
  Tabs,
  Card,
  Row,
  Col,
  Statistic,
  Badge,
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  EyeOutlined,
  BarChartOutlined,
  FileTextOutlined,
  ReconciliationOutlined,
  CheckOutlined,
  SyncOutlined,
  ClockCircleOutlined,
  LockOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { hasPermission } from '../../utils/permissions';
import {
  fetchQuotationsRequest,
} from '../../redux/actions/quotationActions';
import { fetchVendors } from '../../redux/actions/vendorActions';
import { fetchProducts } from '../../redux/actions/productActions';
import QuotationForm from './QuotationForm';
import QuotationDetail, { getQuotationStatusTag } from './QuotationDetail';
import ComparativeStatement from './ComparativeStatement';

const { Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const fmtMoney = (v) => (v != null ? `₹ ${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '—');
const fmtDate  = (v) => (v ? dayjs(v).format('DD MMM YYYY') : '—');

const QuotationList = () => {
  const dispatch = useDispatch();
  const permissions = useSelector((s) => s.auth.permissions || []);
  const { quotations, loading } = useSelector((s) => s.quotations);
  const { vendors } = useSelector((s) => s.vendors || {});

  const canCreate = hasPermission(permissions, 'purchase_order/create');
  const canView   = hasPermission(permissions, 'purchase_order/view');

  const [activeTab, setActiveTab]         = useState('list');
  const [formOpen, setFormOpen]           = useState(false);
  const [detailId, setDetailId]           = useState(null);
  const [statusFilter, setStatusFilter]   = useState('');
  const [vendorFilter, setVendorFilter]   = useState('');
  const [dateRange, setDateRange]         = useState(null);

  const loadList = useCallback(() => {
    const params = {};
    if (statusFilter)    params.status    = statusFilter;
    if (vendorFilter)    params.vendor_id = vendorFilter;
    if (dateRange?.[0])  params.date_from = dateRange[0].format('YYYY-MM-DD');
    if (dateRange?.[1])  params.date_to   = dateRange[1].format('YYYY-MM-DD');
    dispatch(fetchQuotationsRequest(params));
  }, [dispatch, statusFilter, vendorFilter, dateRange]);

  useEffect(() => {
    if (canView) {
      dispatch(fetchVendors());
      dispatch(fetchProducts());
      loadList();
    }
  }, [canView]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!canView) {
    return (
      <div className="page-container">
        <div style={{ textAlign: 'center', padding: 60, color: '#8c8c8c' }}>
          <LockOutlined /> You don't have permission to view quotations.
        </div>
      </div>
    );
  }

  // Summary stats
  const activeCount    = quotations.filter((q) => q.status === 'received').length;
  const convertedCount = quotations.filter((q) => q.status === 'converted').length;
  const expiredCount   = quotations.filter((q) => q.status === 'expired').length;

  const columns = [
    {
      title: 'Quotation #',
      dataIndex: 'quotation_number',
      key: 'quotation_number',
      width: 160,
      render: (v) => <Text style={{ color: '#1890ff', fontWeight: 600 }}>{v}</Text>,
    },
    {
      title: 'Vendor',
      dataIndex: 'vendor_name',
      key: 'vendor_name',
      width: 160,
      ellipsis: true,
      render: (v) => <Tooltip title={v}><span>{v}</span></Tooltip>,
    },
    {
      title: 'Date',
      dataIndex: 'quotation_date',
      key: 'quotation_date',
      width: 110,
      render: fmtDate,
      sorter: (a, b) => new Date(a.quotation_date) - new Date(b.quotation_date),
    },
    {
      title: 'Valid Until',
      dataIndex: 'valid_until',
      key: 'valid_until',
      width: 110,
      render: (v) => {
        if (!v) return <Text type="secondary">—</Text>;
        const expired = dayjs(v).isBefore(dayjs(), 'day');
        return (
          <span style={{ color: expired ? '#ff4d4f' : 'inherit' }}>
            {fmtDate(v)}
          </span>
        );
      },
    },
    {
      title: 'Items',
      key: 'items',
      width: 70,
      align: 'center',
      render: (_, r) => <Tag color="blue">{r.items?.length || 0}</Tag>,
    },
    {
      title: 'Total Amount',
      dataIndex: 'total_amount',
      key: 'total_amount',
      width: 130,
      align: 'right',
      render: (v) => <Text strong style={{ color: '#1890ff' }}>{fmtMoney(v)}</Text>,
      sorter: (a, b) => a.total_amount - b.total_amount,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: getQuotationStatusTag,
    },
    {
      title: 'Created By',
      dataIndex: 'created_by_name',
      key: 'created_by_name',
      width: 120,
      ellipsis: true,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 90,
      fixed: 'right',
      render: (_, record) => (
        <Tooltip title="View Details">
          <Button
            type="primary"
            ghost
            size="small"
            icon={<EyeOutlined />}
            onClick={() => setDetailId(record.id)}
          />
        </Tooltip>
      ),
    },
  ];

  const tabItems = [
    {
      key: 'list',
      label: (
        <Space>
          <FileTextOutlined />
          Quotations
          {activeCount > 0 && <Badge count={activeCount} size="small" />}
        </Space>
      ),
      children: (
        <div>
          {/* Filters */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
            <Space wrap>
              <Select
                placeholder="Filter by status"
                style={{ width: 160 }}
                allowClear
                value={statusFilter || undefined}
                onChange={(v) => setStatusFilter(v || '')}
              >
                <Option value="received">🔵 Received</Option>
                <Option value="expired">🟠 Expired</Option>
                <Option value="cancelled">🔴 Cancelled</Option>
                <Option value="converted">🟢 Converted</Option>
              </Select>
              <Select
                showSearch
                placeholder="Filter by vendor"
                style={{ width: 200 }}
                allowClear
                value={vendorFilter || undefined}
                onChange={(v) => setVendorFilter(v || '')}
                filterOption={(input, option) =>
                  option?.children?.toLowerCase().includes(input.toLowerCase())
                }
              >
                {(vendors || []).map((v) => (
                  <Option key={v.id} value={v.id}>{v.firm_name}</Option>
                ))}
              </Select>
              <RangePicker
                style={{ width: 240 }}
                format="DD MMM YYYY"
                value={dateRange}
                onChange={setDateRange}
                allowClear
                placeholder={['From', 'To']}
              />
              <Button type="primary" onClick={loadList} icon={<ReloadOutlined />} loading={loading}>
                Filter
              </Button>
              <Button icon={<ReloadOutlined />} onClick={() => {
                setStatusFilter(''); setVendorFilter(''); setDateRange(null);
                dispatch(fetchQuotationsRequest({}));
              }}>
                Clear
              </Button>
            </Space>
            {canCreate && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setFormOpen(true)}>
                New Quotation
              </Button>
            )}
          </div>

          <Table
            columns={columns}
            dataSource={quotations}
            rowKey="id"
            loading={loading}
            size="small"
            scroll={{ x: 1100 }}
            pagination={{ pageSize: 15, showSizeChanger: false, showTotal: (t) => `${t} quotations` }}
            locale={{
              emptyText: (
                <div style={{ padding: 40, textAlign: 'center', color: '#8c8c8c' }}>
                  No quotations found. Create a quotation to start comparing vendor rates.
                </div>
              ),
            }}
          />
        </div>
      ),
    },
    {
      key: 'comparative',
      label: (
        <Space>
          <BarChartOutlined />
          Comparative Statement
        </Space>
      ),
      children: <ComparativeStatement />,
    },
  ];

  return (
    <div className="page-container">
      <div className="page-content">
        {/* ── Header ───────────────────────────────────────────────── */}
        <div className="page-header">
          <div className="page-header-title">
            <div className="page-header-icon"><ReconciliationOutlined /></div>
            <div className="page-header-text">
              <h1>Quotations</h1>
              <p>Compare vendor quotes before raising a Purchase Order</p>
            </div>
          </div>
        </div>

        {/* ── Summary cards ─────────────────────────────────────────── */}
        <div className="stats-grid">
          <div className="stat-card" style={{ borderLeftColor: '#1890ff' }}>
            <div className="stat-icon" style={{ background: '#e6f4ff', color: '#1890ff' }}><FileTextOutlined /></div>
            <div className="stat-content">
              <div className="stat-value" style={{ color: '#1890ff' }}>{quotations.length}</div>
              <div className="stat-label">Total Quotations</div>
            </div>
          </div>
          <div className="stat-card" style={{ borderLeftColor: '#1890ff' }}>
            <div className="stat-icon" style={{ background: '#e6f4ff', color: '#1890ff' }}><CheckOutlined /></div>
            <div className="stat-content">
              <div className="stat-value" style={{ color: '#1890ff' }}>{activeCount}</div>
              <div className="stat-label">Active (Received)</div>
            </div>
          </div>
          <div className="stat-card" style={{ borderLeftColor: '#52c41a' }}>
            <div className="stat-icon" style={{ background: '#f6ffed', color: '#52c41a' }}><SyncOutlined /></div>
            <div className="stat-content">
              <div className="stat-value" style={{ color: '#52c41a' }}>{convertedCount}</div>
              <div className="stat-label">Converted to PO</div>
            </div>
          </div>
          <div className="stat-card" style={{ borderLeftColor: '#faad14' }}>
            <div className="stat-icon" style={{ background: '#fffbe6', color: '#faad14' }}><ClockCircleOutlined /></div>
            <div className="stat-content">
              <div className="stat-value" style={{ color: '#faad14' }}>{expiredCount}</div>
              <div className="stat-label">Expired</div>
            </div>
          </div>
        </div>

        {/* ── Tabs ──────────────────────────────────────────────────── */}
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          tabBarExtraContent={
            activeTab === 'list' ? (
              <Button icon={<ReloadOutlined />} onClick={loadList} loading={loading}>
                Refresh
              </Button>
            ) : null
          }
        />
      </div>

      {/* Modals */}
      <QuotationForm
        open={formOpen}
        onClose={() => { setFormOpen(false); loadList(); }}
      />
      {detailId && (
        <QuotationDetail
          open={!!detailId}
          onClose={() => { setDetailId(null); loadList(); }}
          quotationId={detailId}
        />
      )}
    </div>
  );
};

export default QuotationList;
